/**
 * 底图大地基准注册表（纠偏唯一入口）
 *
 * 业务数据一律 WGS84。显示层跟着当前底图走，不要在功能代码里写
 * `if (高德) wgs84ToGcj02`。
 *
 * 新增一种要纠偏的底图时，任选其一即可（全图元素会跟着走）：
 * 1. 目录里给该底图写 `datum: 'GCJ02'`（推荐）
 * 2. 或在 BASEMAP_DATUM_RULES 加一条匹配
 * 3. 若是新基准：先在 DATUM_PROFILES 登记 toDisplay/fromDisplay/warpDem（BD09 已登记，暂无底图使用）
 *
 * 上图请用 getMapCoordAdapter / toMapLngLat / toMapGeometry，不要直接调 coordTransform。
 */

import { wgs84ToGcj02, gcj02ToWgs84 } from '../coordTransform';
import coordDatum from '../coordDatum/coordDatum.js';

export const DATUM_WGS84 = 'WGS84';
export const DATUM_GCJ02 = 'GCJ02';
export const DATUM_BD09 = 'BD09';
export const MAP_DATUM_EVENT = 'zhxt:map-datum';

function pairFromPoint(p) {
  return [p.lng, p.lat];
}

function identityPair(lng, lat) {
  return [Number(lng), Number(lat)];
}

function gcjToDisplay(lng, lat) {
  return pairFromPoint(wgs84ToGcj02(lng, lat));
}

function gcjFromDisplay(lng, lat) {
  return pairFromPoint(gcj02ToWgs84(lng, lat, 5));
}

function gcjFromDisplayFast(lng, lat) {
  return pairFromPoint(gcj02ToWgs84(lng, lat, 2));
}

function bdToDisplay(lng, lat) {
  return coordDatum.convertPair(lng, lat, DATUM_WGS84, DATUM_BD09);
}

function bdFromDisplay(lng, lat) {
  return coordDatum.convertPair(lng, lat, DATUM_BD09, DATUM_WGS84, { iterations: 5 });
}

function bdFromDisplayFast(lng, lat) {
  return coordDatum.convertPair(lng, lat, DATUM_BD09, DATUM_WGS84, { iterations: 2 });
}

/**
 * 每种显示基准的能力。warp* 为 true 时，对应元素上图必须走 toDisplay / DEM 纠偏源。
 */
export const DATUM_PROFILES = {
  [DATUM_WGS84]: {
    id: DATUM_WGS84,
    warpDisplay: false,
    warpVectors: false,
    warpDem: false,
    toDisplay: identityPair,
    fromDisplay: identityPair,
    fromDisplayFast: identityPair
  },
  [DATUM_GCJ02]: {
    id: DATUM_GCJ02,
    warpDisplay: true,
    warpVectors: true,
    warpDem: true,
    toDisplay: gcjToDisplay,
    fromDisplay: gcjFromDisplay,
    fromDisplayFast: gcjFromDisplayFast
  },
  /** 百度 BD09：算法在 shared/coord-datum；目录里尚无 BD09 底图，登记后不影响现有底图 */
  [DATUM_BD09]: {
    id: DATUM_BD09,
    warpDisplay: true,
    warpVectors: true,
    warpDem: true,
    toDisplay: bdToDisplay,
    fromDisplay: bdFromDisplay,
    fromDisplayFast: bdFromDisplayFast
  }
};

/**
 * 未在目录写 datum 时的兜底匹配（id / provider）。
 * 目录显式 datum 优先于本表。
 */
export const BASEMAP_DATUM_RULES = [
  {
    datum: DATUM_GCJ02,
    test({ id, provider }) {
      const p = String(provider || '').toLowerCase();
      if (p === 'gaode' || p === 'amap' || p === 'google') return true;
      const s = String(id || '').toLowerCase();
      return (
        s.startsWith('gaode')
        || s.includes('gaode-')
        || s.startsWith('amap')
        || s.includes('amap-')
        || s.startsWith('google')
        || s.includes('google-')
      );
    }
  }
];

/** id → { provider, datum }，由地图目录启动时 registerBasemapDatums 填入 */
const catalogHints = new Map();

export function registerBasemapDatums(entries) {
  (Array.isArray(entries) ? entries : []).forEach((item) => {
    if (!item?.id) return;
    catalogHints.set(String(item.id), {
      provider: item.provider || null,
      datum: item.datum || null
    });
  });
}

export function getDatumProfile(datumId) {
  return DATUM_PROFILES[datumId] || DATUM_PROFILES[DATUM_WGS84];
}

export function resolveBasemapDatum(basemapId, meta = {}) {
  const id = String(basemapId || '');
  const hint = catalogHints.get(id) || {};
  const merged = {
    id,
    provider: meta.provider || hint.provider,
    datum: meta.datum || hint.datum
  };
  if (merged.datum && DATUM_PROFILES[merged.datum]) {
    return merged.datum;
  }
  for (let i = 0; i < BASEMAP_DATUM_RULES.length; i += 1) {
    const rule = BASEMAP_DATUM_RULES[i];
    try {
      if (rule.test(merged)) return rule.datum;
    } catch (_) {
      /* 下一条 */
    }
  }
  return DATUM_WGS84;
}

function finitePair(lng, lat, fn) {
  const lngN = Number(lng);
  const latN = Number(lat);
  if (!Number.isFinite(lngN) || !Number.isFinite(latN)) return null;
  const out = fn(lngN, latN);
  if (!out || !Number.isFinite(Number(out[0])) || !Number.isFinite(Number(out[1]))) {
    return [lngN, latN];
  }
  return [Number(out[0]), Number(out[1])];
}

function mapPos(pt, pairFn) {
  if (!pt || pt.length < 2) return pt;
  const out = finitePair(pt[0], pt[1], pairFn);
  if (!out) return pt;
  if (pt.length === 2) return out;
  return [out[0], out[1], ...pt.slice(2)];
}

/** 纯函数：按 pairFn 转换 GeoJSON 几何（WGS84 ↔ 显示坐标） */
export function transformGeometry(geom, pairFn) {
  if (!geom || !geom.type || typeof pairFn !== 'function') return geom;
  if (geom.type === 'Point') {
    return { ...geom, coordinates: mapPos(geom.coordinates, pairFn) };
  }
  if (geom.type === 'MultiPoint' || geom.type === 'LineString') {
    return {
      ...geom,
      coordinates: (geom.coordinates || []).map((pt) => mapPos(pt, pairFn))
    };
  }
  if (geom.type === 'MultiLineString' || geom.type === 'Polygon') {
    return {
      ...geom,
      coordinates: (geom.coordinates || []).map((ring) =>
        (ring || []).map((pt) => mapPos(pt, pairFn))
      )
    };
  }
  if (geom.type === 'MultiPolygon') {
    return {
      ...geom,
      coordinates: (geom.coordinates || []).map((poly) =>
        (poly || []).map((ring) => (ring || []).map((pt) => mapPos(pt, pairFn)))
      )
    };
  }
  if (geom.type === 'GeometryCollection') {
    return {
      ...geom,
      geometries: (geom.geometries || []).map((g) => transformGeometry(g, pairFn))
    };
  }
  return geom;
}

export function transformGeoJSON(geojson, pairFn) {
  if (!geojson) return geojson;
  if (geojson.type === 'Feature') {
    return {
      ...geojson,
      geometry: transformGeometry(geojson.geometry, pairFn)
    };
  }
  if (geojson.type === 'FeatureCollection') {
    return {
      ...geojson,
      features: (geojson.features || []).map((f) => transformGeoJSON(f, pairFn))
    };
  }
  return transformGeometry(geojson, pairFn);
}

export function createCoordAdapter(basemapId, meta = {}) {
  const datum = resolveBasemapDatum(basemapId, meta);
  const profile = getDatumProfile(datum);
  return {
    datum: profile.id,
    basemapId: String(basemapId || ''),
    warpDisplay: profile.warpDisplay,
    warpVectors: profile.warpVectors,
    warpDem: profile.warpDem,
    toDisplay: profile.toDisplay,
    fromDisplay: profile.fromDisplay,
    fromDisplayFast: profile.fromDisplayFast || profile.fromDisplay,
    toDisplayLngLat(lng, lat) {
      return finitePair(lng, lat, profile.toDisplay);
    },
    fromDisplayLngLat(lng, lat) {
      return finitePair(lng, lat, profile.fromDisplay);
    },
    toDisplayGeometry(geom) {
      if (!profile.warpVectors) return geom;
      return transformGeometry(geom, profile.toDisplay);
    },
    toDisplayGeoJSON(geojson) {
      if (!profile.warpVectors) return geojson;
      return transformGeoJSON(geojson, profile.toDisplay);
    }
  };
}
